<template>
  <!-- Conditionally wrap with panel-navigation based on embed mode -->
  <component
    :is="isEmbedMode ? 'div' : 'panel-navigation'"
    v-bind="!isEmbedMode ? { expandable: true, customPadding: '20px 30px', mobileEnabled: true } : {}"
    :class="isEmbedMode ? 'embed-container' : ''"
  >
    <!-- Delete Confirmation Dialog -->
    <delete-confirm-dialog
      v-model="showDeleteDialog"
      :item-count="deleteItemCount"
      :loading="deleting"
      @confirm="confirmDelete"
      @cancel="cancelDelete"
    />
    
    <!-- Share Modal -->
    <share-modal
      v-if="showShareModal"
      :visible="showShareModal"
      :scan-id="shareScanId"
      :scan-type="shareScanType"
      @close="closeShareModal"
      @copied="onShareCopied"
    />
    
    <!-- Header with NEW SCAN button (Keep original layout) -->
    <div class="float-right ml-5 mt-3 content-section">
      <div class="border-1 text-white text-xs text-bold px-4 py-2 rounded cursor-pointer float-right ml-2" @click="newScan()" style="margin-top: -5px; background-color: #2477f6; border-color: #2477f6;">
        <i class="fa fa-plus mr-2"></i> NEW SCAN
      </div>
    </div>

    <h1 class="text-lg font-semibold mb-3">Scans</h1>
    <p class="text-gray-600 text-sm mb-3 hidden md:block">View and manage your recent scan activity</p>

    <hr class="border-b border-gray-100 mb-3">

    <v-skeleton-loader v-if="fetchingData" type="article, table, table"></v-skeleton-loader>

    <!-- Stats Cards -->
    <div class="-mx-2" v-if="!fetchingData">
      <div class="stats-grid flex flex-row flex-wrap mb-4">
        <!-- Total Scans Card -->
        <div class="dashboard-box stats-box w-full px-2 md:w-1/3 lg:w-1/3">
          <v-card class="pa-2" tile>
            <div class="p-3">
              <div class="dashboard-box-title">Total Scans</div>
              <div class="stats-box-number">{{ scanStats.totalScans }}</div>
              <div class="stats-box-icon-bg"></div>
              <div class="stats-box-icon">
                <i class="mdi mdi-view-grid-outline"></i>
              </div>
            </div>
          </v-card>
        </div>

        <!-- Quick Scans Card -->
        <div class="dashboard-box stats-box w-full px-2 md:w-1/3 lg:w-1/3">
          <v-card class="pa-2" tile>
            <div class="p-3">
              <div class="dashboard-box-title">Quick Scans</div>
              <div class="stats-box-number">{{ scanStats.quickScans }}</div>
              <div class="stats-box-icon-bg"></div>
              <div class="stats-box-icon">
                <i class="mdi mdi-map-marker"></i>
              </div>
            </div>
          </v-card>
        </div>

        <!-- Recurring Scans Card -->
        <div class="dashboard-box stats-box w-full px-2 md:w-1/3 lg:w-1/3">
          <v-card class="pa-2" tile>
            <div class="p-3">
              <div class="dashboard-box-title">Recurring scans</div>
              <div class="stats-box-number">{{ scanStats.recurringScans }}</div>
              <div class="stats-box-icon-bg"></div>
              <div class="stats-box-icon">
                <i class="mdi mdi-history"></i>
              </div>
            </div>
          </v-card>
        </div>
      </div>
    </div>

    <!-- Table Section -->
    <div v-if="!fetchingData && hasData" class="dashboard-box mt-10">
      <v-card class="pa-2" tile>
        <div class="p-3">
          <div class="w-full mb-4">
            <!-- Filters Row -->
            <div class="filters-row flex flex-row items-center mb-4">
              <!-- Search Box -->
              <v-text-field
                v-model="searchQuery"
                placeholder="Search"
                prepend-inner-icon="mdi-magnify"
                outlined
                dense
                hide-details
                class="search-field mr-3"
                style="max-width: 300px;"
              ></v-text-field>

              <!-- Scan Type Filter -->
              <div class="custom-select-wrapper filter-dropdown mr-3">
                <div
                  class="custom-select"
                  @click.stop="toggleScanTypeDropdown"
                  :class="{ active: showScanTypeDropdown }"
                >
                  <span class="custom-select-label">{{ selectedScanTypeLabel }}</span>
                  <i class="mdi mdi-chevron-down custom-select-arrow"></i>
                </div>
                <div
                  v-if="showScanTypeDropdown"
                  class="custom-select-dropdown"
                  @click.stop
                >
                  <div
                    v-for="option in scanTypeOptions"
                    :key="option.value"
                    class="custom-select-option"
                    :class="{ selected: scanTypeFilter === option.value }"
                    @click="selectScanType(option.value)"
                  >
                    {{ option.text }}
                  </div>
                </div>
              </div>

              <!-- Filter Button -->
              <v-btn icon class="filter-btn mr-3">
                <v-icon>mdi-filter-variant</v-icon>
              </v-btn>

              <v-spacer></v-spacer>

              <!-- Actions Dropdown -->
              <div class="custom-actions-wrapper actions-dropdown">
                <div
                  class="custom-actions-button"
                  @click.stop="toggleActionsDropdown"
                  :class="{ active: showActionsDropdown }"
                >
                  <span class="mr-2">Actions</span>
                  <i class="mdi mdi-chevron-down custom-actions-arrow"></i>
                </div>
                <div
                  v-if="showActionsDropdown"
                  class="custom-actions-dropdown"
                  @click.stop
                >
                  <div
                    class="custom-actions-option"
                    @click="bulkDelete()"
                  >
                    <span class="table-menu-action-icon"><i class="fa fa-trash"></i></span>
                    Delete Selected
                  </div>
                </div>
              </div>
            </div>

            <!-- Data Table -->
            <!-- Skeleton Loader -->
            <v-skeleton-loader
              v-if="fetchingTable"
              type="table"
              class="mt-4"
            ></v-skeleton-loader>

            <div class="table-container">
            <div class="table-responsive">
            <table class="w-full rg-datatable mt-4" v-if="!fetchingTable">
              <thead class="border-b border-gray-100 w-full">
                <tr>
                  <th></th>
                  <th class="sortable-header" @click="toggleSort('scanType')">
                    <div class="header-content">
                      <span>Scan Type</span>
                      <svg class="sort-icon" :class="{ 'active-asc': sortBy === 'scanType' && !sortDesc, 'active-desc': sortBy === 'scanType' && sortDesc }" width="10" height="12" viewBox="0 0 6 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path fill-rule="evenodd" clip-rule="evenodd" d="M3 0.5L0 4.5H6L3 0.5Z" :fill="sortBy === 'scanType' && !sortDesc ? '#2477f6' : '#757B89'"/>
                        <path fill-rule="evenodd" clip-rule="evenodd" d="M3 9.5L0 5.5H6L3 9.5Z" :fill="sortBy === 'scanType' && sortDesc ? '#2477f6' : '#757B89'"/>
                      </svg>
                    </div>
                  </th>
                  <th class="sortable-header" @click="toggleSort('businessName')">
                    <div class="header-content">
                      <span>Tracked Business</span>
                      <svg class="sort-icon" :class="{ 'active-asc': sortBy === 'businessName' && !sortDesc, 'active-desc': sortBy === 'businessName' && sortDesc }" width="10" height="12" viewBox="0 0 6 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path fill-rule="evenodd" clip-rule="evenodd" d="M3 0.5L0 4.5H6L3 0.5Z" :fill="sortBy === 'businessName' && !sortDesc ? '#2477f6' : '#757B89'"/>
                        <path fill-rule="evenodd" clip-rule="evenodd" d="M3 9.5L0 5.5H6L3 9.5Z" :fill="sortBy === 'businessName' && sortDesc ? '#2477f6' : '#757B89'"/>
                      </svg>
                    </div>
                  </th>
                  <th class="sortable-header" @click="toggleSort('keywords')">
                    <div class="header-content">
                      <span>Keyword(s)</span>
                      <svg class="sort-icon" :class="{ 'active-asc': sortBy === 'keywords' && !sortDesc, 'active-desc': sortBy === 'keywords' && sortDesc }" width="10" height="12" viewBox="0 0 6 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path fill-rule="evenodd" clip-rule="evenodd" d="M3 0.5L0 4.5H6L3 0.5Z" :fill="sortBy === 'keywords' && !sortDesc ? '#2477f6' : '#757B89'"/>
                        <path fill-rule="evenodd" clip-rule="evenodd" d="M3 9.5L0 5.5H6L3 9.5Z" :fill="sortBy === 'keywords' && sortDesc ? '#2477f6' : '#757B89'"/>
                      </svg>
                    </div>
                  </th>
                  <th class="sortable-header" @click="toggleSort('gridSize')">
                    <div class="header-content">
                      <span>Scan Size</span>
                      <svg class="sort-icon" :class="{ 'active-asc': sortBy === 'gridSize' && !sortDesc, 'active-desc': sortBy === 'gridSize' && sortDesc }" width="10" height="12" viewBox="0 0 6 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path fill-rule="evenodd" clip-rule="evenodd" d="M3 0.5L0 4.5H6L3 0.5Z" :fill="sortBy === 'gridSize' && !sortDesc ? '#2477f6' : '#757B89'"/>
                        <path fill-rule="evenodd" clip-rule="evenodd" d="M3 9.5L0 5.5H6L3 9.5Z" :fill="sortBy === 'gridSize' && sortDesc ? '#2477f6' : '#757B89'"/>
                      </svg>
                    </div>
                  </th>
                  <th class="sortable-header" @click="toggleSort('avgRank')">
                    <div class="header-content">
                      <span>Avg. Rank</span>
                      <svg class="sort-icon" :class="{ 'active-asc': sortBy === 'avgRank' && !sortDesc, 'active-desc': sortBy === 'avgRank' && sortDesc }" width="10" height="12" viewBox="0 0 6 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path fill-rule="evenodd" clip-rule="evenodd" d="M3 0.5L0 4.5H6L3 0.5Z" :fill="sortBy === 'avgRank' && !sortDesc ? '#2477f6' : '#757B89'"/>
                        <path fill-rule="evenodd" clip-rule="evenodd" d="M3 9.5L0 5.5H6L3 9.5Z" :fill="sortBy === 'avgRank' && sortDesc ? '#2477f6' : '#757B89'"/>
                      </svg>
                    </div>
                  </th>
                  <th class="sortable-header" @click="toggleSort('createdAt')">
                    <div class="header-content">
                      <span>Date Created</span>
                      <svg class="sort-icon" :class="{ 'active-asc': sortBy === 'createdAt' && !sortDesc, 'active-desc': sortBy === 'createdAt' && sortDesc }" width="10" height="12" viewBox="0 0 6 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path fill-rule="evenodd" clip-rule="evenodd" d="M3 0.5L0 4.5H6L3 0.5Z" :fill="sortBy === 'createdAt' && !sortDesc ? '#2477f6' : '#757B89'"/>
                        <path fill-rule="evenodd" clip-rule="evenodd" d="M3 9.5L0 5.5H6L3 9.5Z" :fill="sortBy === 'createdAt' && sortDesc ? '#2477f6' : '#757B89'"/>
                      </svg>
                    </div>
                  </th>
                  <th class="sortable-header" @click="toggleSort('nextRunAt')">
                    <div class="header-content">
                      <span>Schedule</span>
                      <svg class="sort-icon" :class="{ 'active-asc': sortBy === 'nextRunAt' && !sortDesc, 'active-desc': sortBy === 'nextRunAt' && sortDesc }" width="10" height="12" viewBox="0 0 6 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path fill-rule="evenodd" clip-rule="evenodd" d="M3 0.5L0 4.5H6L3 0.5Z" :fill="sortBy === 'nextRunAt' && !sortDesc ? '#2477f6' : '#757B89'"/>
                        <path fill-rule="evenodd" clip-rule="evenodd" d="M3 9.5L0 5.5H6L3 9.5Z" :fill="sortBy === 'nextRunAt' && sortDesc ? '#2477f6' : '#757B89'"/>
                      </svg>
                    </div>
                  </th>
                  <th></th>
                </tr>
              </thead>
              <tbody class="w-full" style="">
                <!-- Dynamic Rows -->
                <tr v-for="scan in scans" :key="scan.id">
                  <td class="checkbox-cell">
                    <v-checkbox
                      :id="scan.id + '-checkbox'"
                      :value="scan.id"
                      v-model="selectedIds"
                      hide-details
                      :ripple="false"
                    ></v-checkbox>
                  </td>
                  <td>
                    <v-chip small :style="scan.scanType === 'Quick Scan' ? 'background: #e6f4ea; color: #10b981;' : 'background: #e3f2fd; color: #1976d2;'" class="text-xs">
                     {{ scan.scanType }}
                    </v-chip>
                  </td>
                  <td>
                    <div style="max-width: 250px;">
                      <div class="business-name">{{ scan.businessName || '-' }}</div>
                      <div class="text-xs text-gray-500" style="word-wrap: break-word; white-space: normal;">{{ scan.address || '-' }}</div>
                    </div>
                  </td>
                  <td>
                    <div v-if="scan.keywords && scan.keywords.length > 0" style="display: inline-flex; align-items: center; gap: 4px;">
                      <v-chip style="background-color: #19233A0D; color:#19233A; text-transform: capitalize;" small>
                        {{ scan.keywords[0] }}
                      </v-chip>
                      <v-chip 
                        v-if="scan.keywords.length > 1" 
                        style="background-color: #19233A0D; color: #19233A; cursor: pointer;" 
                        small
                        @mouseenter="showTooltip($event, scan.keywords.slice(1))"
                        @mouseleave="hideTooltip">
                        +{{ scan.keywords.length - 1 }}
                      </v-chip>
                    </div>
                    <span v-else>-</span>
                  </td>
                  <td>
                    <span class="text-gray-600 flex">
                      <template>
                        <!-- Grid shape icon -->
                        <svg v-if="scan.grid_shape === 'circle'" width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" style="display: inline-block; vertical-align: middle; margin-right: 4px;">
                          <circle cx="9" cy="9" r="8.5" stroke="#757B89"/>
                        </svg>
                        <svg v-else width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" style="display: inline-block; vertical-align: middle; margin-right: 4px;">
                          <rect x="0.5" y="0.5" width="15" height="15" rx="1.5" stroke="#757B89"/>
                        </svg>
                        {{ scan.gridSize || '3 x 3' }}

                        <i class="mdi mdi-circle-small text-xs"></i>

                        <!-- Location pin icon for radius -->
                        <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" style="display: inline-block; vertical-align: middle; margin-right: 2px;">
                          <path d="M9 1C5.68629 1 3 3.68629 3 7C3 11.25 9 17 9 17C9 17 15 11.25 15 7C15 3.68629 12.3137 1 9 1ZM9 9.5C7.61929 9.5 6.5 8.38071 6.5 7C6.5 5.61929 7.61929 4.5 9 4.5C10.3807 4.5 11.5 5.61929 11.5 7C11.5 8.38071 10.3807 9.5 9 9.5Z" fill="#757B89"/>
                        </svg>
                        {{ formatRadius(scan) }}
                      </template>
                    </span>
                  </td>
                  <td>
                    <!-- Quick Scan: Show Processing if not completed -->
                    <template v-if="scan.scanType === 'Quick Scan' && scan.status !== 'completed'">
                      <v-chip small style="background: #e6f4ea; color: #10b981;" class="text-xs">
                        Processing
                      </v-chip>
                    </template>
                    <!-- Recurring Scan: Show Processing if status is processing, otherwise show avgRank or N/A -->
                    <template v-else-if="scan.scanType === 'Recurring'">
                      <template v-if="scan.status === 'processing'">
                        <v-chip small style="background: #e6f4ea; color: #10b981;" class="text-xs">
                          Processing
                        </v-chip>
                      </template>
                      <template v-else-if="scan.hasExecuted === false || scan.avgRank === null">
                        N/A
                      </template>
                      <template v-else>
                        {{ scan.avgRank }}
                      </template>
                    </template>
                    <!-- Default: Show avgRank or N/A -->
                    <template v-else>
                      {{ scan.avgRank || 'N/A' }}
                    </template>
                  </td>
                  <td>{{ formatDate(scan.createdAt) }}</td>
                  <td>
                    <div v-if="scan.scanType === 'Recurring'">
                      <template v-if="scan.frequency && scan.frequency.toLowerCase() === 'manual'">
                        <v-chip small style="background: #f3f4f6; color: #6b7280;" class="text-xs">
                          Manual
                        </v-chip>
                      </template>
                      <template v-else-if="scan.nextRunAt">
                        <div 
                          style="cursor: pointer;"
                          @mouseenter="showLastRunTooltip($event, scan)"
                          @mouseleave="hideLastRunTooltip">
                          <i class="mdi mdi-clock-outline text-xs"></i> Next Scan
                        </div>
                        <div class="text-xs text-gray-500">
                          {{ formatDate(scan.nextRunAt) }}
                        </div>
                      </template>
                      <template v-else>-</template>
                    </div>
                    <span v-else>-</span>
                  </td>
                  <td style="text-align: right;">
                    <div class="action-buttons">
                      <!-- View Grid Button -->
                      <button
                        @click="viewGrid(scan)"
                        class="action-btn-with-text"
                      >
                        <i class="mdi mdi-view-grid-outline"></i>
                        <span>View Grid</span>
                      </button>

                      <!-- View Report Button -->
                      <button
                        @click="viewReport(scan)"
                        class="action-btn-with-text"
                      >
                        <i class="mdi mdi-file-chart-outline"></i>
                        <span>View Report</span>
                      </button>

                      <!-- Three Dots Menu -->
                      <div class="custom-dropdown" :class="{ active: activeDropdown === scan.id }">
                        <i
                          class="mdi mdi-dots-vertical"
                          style="font-size: 14px; cursor: pointer; color: rgb(117, 117, 117);"
                          @click.stop="toggleDropdown(scan.id, $event)"
                        ></i>
                      <div class="custom-dropdown-content"
                           v-if="activeDropdown === scan.id"
                           @click.stop
                           :style="getDropdownStyle(scan.id)"
                           :ref="`dropdown-${scan.id}`">
                        <v-list class="table-menu-actions">
                          <!-- Quick Scan Options -->
                          <template v-if="scan.scanType === 'Quick Scan'">
                            <v-list-item class="hover:bg-gray-200">
                              <v-list-item-title>
                                <a href="javascript: void(0);" @click="rerunScan(scan.id)">
                                  <span class="table-menu-action-icon"><i class="mdi mdi-refresh"></i></span> Rescan
                                </a>
                              </v-list-item-title>
                            </v-list-item>
                            <v-list-item class="hover:bg-gray-200">
                              <v-list-item-title>
                                <a href="javascript: void(0);" @click="shareScan(scan.id)">
                                  <span class="table-menu-action-icon"><i class="mdi mdi-share-variant-outline"></i></span> Share
                                </a>
                              </v-list-item-title>
                            </v-list-item>
                            <v-list-item class="hover:bg-gray-200">
                              <v-list-item-title>
                                <a href="javascript: void(0);" @click="exportPNG(scan.id)">
                                  <span class="table-menu-action-icon"><i class="mdi mdi-file-image-outline"></i></span> Export as PNG
                                </a>
                              </v-list-item-title>
                            </v-list-item>
                            <v-list-item class="hover:bg-gray-200">
                              <v-list-item-title>
                                <a href="javascript: void(0);" @click="exportCSV(scan.id)">
                                  <span class="table-menu-action-icon"><i class="mdi mdi-file-delimited-outline"></i></span> Export as CSV
                                </a>
                              </v-list-item-title>
                            </v-list-item>
                            <v-list-item class="hover:bg-gray-200">
                              <v-list-item-title>
                                <a href="javascript: void(0);" @click="deleteScan(scan.id)">
                                  <span class="table-menu-action-icon"><i class="mdi mdi-delete-outline"></i></span> Delete
                                </a>
                              </v-list-item-title>
                            </v-list-item>
                          </template>
                          
                          <!-- Grid Scan (Recurring) Options -->
                          <template v-else>
                            <v-list-item class="hover:bg-gray-200">
                              <v-list-item-title>
                                <a href="javascript: void(0);" @click="editScan(scan.id)">
                                  <span class="table-menu-action-icon"><i class="mdi mdi-pencil-outline"></i></span> Edit
                                </a>
                              </v-list-item-title>
                            </v-list-item>
                            <v-list-item class="hover:bg-gray-200">
                              <v-list-item-title>
                                <a href="javascript: void(0);" @click="duplicateScan(scan.id)">
                                  <span class="table-menu-action-icon"><i class="mdi mdi-content-copy"></i></span> Duplicate
                                </a>
                              </v-list-item-title>
                            </v-list-item>
                            <v-list-item class="hover:bg-gray-200">
                              <v-list-item-title>
                                <a href="javascript: void(0);" @click="shareScan(scan.id)">
                                  <span class="table-menu-action-icon"><i class="mdi mdi-share-variant-outline"></i></span> Share
                                </a>
                              </v-list-item-title>
                            </v-list-item>
                            <v-list-item class="hover:bg-gray-200">
                              <v-list-item-title>
                                <a href="javascript: void(0);" @click="exportPNG(scan.id)">
                                  <span class="table-menu-action-icon"><i class="mdi mdi-file-image-outline"></i></span> Export as PNG
                                </a>
                              </v-list-item-title>
                            </v-list-item>
                            <v-list-item class="hover:bg-gray-200">
                              <v-list-item-title>
                                <a href="javascript: void(0);" @click="exportCSV(scan.id)">
                                  <span class="table-menu-action-icon"><i class="mdi mdi-file-delimited-outline"></i></span> Export as CSV
                                </a>
                              </v-list-item-title>
                            </v-list-item>

                            <v-list-item class="hover:bg-gray-200">
                              <v-list-item-title>
                                <a href="javascript: void(0);" @click="deleteScan(scan.id)">
                                  <span class="table-menu-action-icon"><i class="mdi mdi-delete-outline"></i></span> Delete
                                </a>
                              </v-list-item-title>
                            </v-list-item>
                          </template>
                        </v-list>
                      </div>
                    </div>
                    </div>
                  </td>
                </tr>

              </tbody>
            </table>
            </div>
            </div>

            <!-- Pagination -->
            <v-pagination
              v-if="!fetchingTable && totalPages > 1"
              class="float-right mt-6 -mb-3"
              v-model="page"
              :length="totalPages"
              :total-visible="7"
              color="blue"
              circle
            ></v-pagination>

            <div class="clearfix"></div>
          </div>
        </div>
      </v-card>
    </div>

    <!-- No Data State -->
    <div v-if="!fetchingData && !hasData" class="dashboard-box mt-5">
      <v-card class="pa-2" tile>
        <div class="p-3">
          <div class="w-full mb-4">
            <br /><br /><br /><br />

            <div class="dashboard-box-iconsquare">
              <div class="dashboard-box-icon-background"></div>
              <div class="dashboard-box-icon">
                <i class="mdi mdi-history"></i>
              </div>
            </div>

            <div style="text-align: center;">
              <div class="pt-2 pb-2">
                <strong>No Scans Yet</strong>
              </div>
              <div class="text-gray-600 text-sm">
                You haven't run any scans yet. Launch your first scan to see data.
              </div>
            </div>

            <div style="text-align: center; margin-top: 20px;">
              <div class="border-1 text-white text-xs text-bold px-4 py-2 rounded cursor-pointer inline-block" @click="newScan()" style="background-color: #2477f6; border-color: #2477f6;">
                <i class="fa fa-plus mr-2"></i> NEW SCAN
              </div>
            </div>
            
            <br /><br /><br /><br /><br />
          </div>
        </div>
      </v-card>
    </div>
  </component>
</template>

<script>
import PanelNavigation from '@/components/PanelNavigation.vue'
import DeleteConfirmDialog from '@/components/DeleteConfirmDialog.vue'
import ShareModal from '@/components/ShareModal.vue'
import embedMode from '@/mixins/embedMode'
import axios from 'axios'
import moment from 'moment'

export default {
  mixins: [embedMode],
  metaInfo () {
    return {
      title: "Scans | " + this.$appName
    }
  },
  name: 'Scans',
  components: {
    PanelNavigation,
    DeleteConfirmDialog,
    ShareModal
  },
  data() {
    return {
      fetchingData: false,
      fetchingTable: false,
      hasData: true, // Toggle this to false to see no-data state
      scans: [],
      originalScans: [], // Store original order from backend
      selectedIds: [],
      selectAll: false,
      searchQuery: '',
      searchDebounce: null,
      scanTypeFilter: 'all',
      refreshInterval: null, // Timer for auto-refresh
      scanTypeOptions: [
        { text: 'Scan Types', value: 'all' },
        { text: 'Quick Scan', value: 'quick' },
        { text: 'Recurring', value: 'recurring' }
      ],
      page: 1,
      totalPages: 1,
      totalItems: 0,
      itemsPerPage: 10,
      // Sorting
      sortBy: 'createdAt',
      sortDesc: true,
      // Stats data
      scanStats: {
        totalScans: 0,
        quickScans: 0,
        recurringScans: 0
      },
      // Delete dialog
      showDeleteDialog: false,
      deleteItemCount: 0,
      deleting: false,
      deleteIds: [],
      deleteType: 'single', // 'single' or 'bulk'
      // Share modal
      showShareModal: false,
      shareScanId: null,
      shareScanType: null,
      // Custom dropdown
      activeDropdown: null,
      shouldOpenUpward: {},
      dropdownPositions: {},
      // Custom select dropdown
      showScanTypeDropdown: false,
      showActionsDropdown: false,
      // Mobile navigation
      mobileNavOpen: false
    }
  },
  computed: {
    selectedScanTypeLabel() {
      const option = this.scanTypeOptions.find(opt => opt.value === this.scanTypeFilter);
      return option ? option.text : 'All Scans';
    },
    isEmbedMode() {
      return this.$store.getters['embed/isEmbedMode'];
    },
    embedToken() {
      return this.$store.getters['embed/embedToken'];
    }
  },
  methods: {
    toggleDropdown(scanId, event) {
      if (this.activeDropdown === scanId) {
        this.activeDropdown = null;
        this.$set(this.shouldOpenUpward, scanId, false);
        this.$set(this.dropdownPositions, scanId, null);
      } else {
        this.activeDropdown = scanId;

        // Calculate dropdown position
        this.$nextTick(() => {
          if (event && event.target) {
            const button = event.target.closest('.custom-dropdown');
            if (!button) return;

            const buttonRect = button.getBoundingClientRect();
            const viewportHeight = window.innerHeight;
            const dropdownHeight = 250; // Approximate height of dropdown menu
            const spaceBelow = viewportHeight - buttonRect.bottom;

            let position = {};

            // If not enough space below, open upward
            if (spaceBelow < dropdownHeight && buttonRect.top > dropdownHeight) {
              // Open upward
              position.bottom = viewportHeight - buttonRect.top + 4;
              position.top = 'auto';
              this.$set(this.shouldOpenUpward, scanId, true);
            } else {
              // Open downward
              position.top = buttonRect.bottom + 4;
              position.bottom = 'auto';
              this.$set(this.shouldOpenUpward, scanId, false);
            }

            // Set horizontal position
            position.right = window.innerWidth - buttonRect.right;
            position.left = 'auto';

            // Store position
            this.$set(this.dropdownPositions, scanId, position);
          }
        });
      }
    },

    getDropdownStyle(scanId) {
      const position = this.dropdownPositions[scanId];
      if (!position) return {};

      return {
        top: typeof position.top === 'number' ? `${position.top}px` : position.top,
        bottom: typeof position.bottom === 'number' ? `${position.bottom}px` : position.bottom,
        right: typeof position.right === 'number' ? `${position.right}px` : position.right,
        left: typeof position.left === 'number' ? `${position.left}px` : position.left
      };
    },
    closeDropdowns() {
      this.activeDropdown = null;
      this.showScanTypeDropdown = false;
      this.showActionsDropdown = false;
    },
    toggleScanTypeDropdown() {
      this.showScanTypeDropdown = !this.showScanTypeDropdown;
      this.showActionsDropdown = false;
    },
    selectScanType(value) {
      this.scanTypeFilter = value;
      this.showScanTypeDropdown = false;
    },
    toggleActionsDropdown() {
      this.showActionsDropdown = !this.showActionsDropdown;
      this.showScanTypeDropdown = false;
    },
    async refreshData() {
      // Silent refresh without showing loading states
      try {
        // Refresh stats (pass false to skip loading state)
        await this.fetchScanStats(false);
        // Refresh scans table (pass false to skip loading state)
        await this.fetchAllScans(false);
      } catch (error) {
        console.error('Error refreshing data:', error);
      }
    },
    async fetchScanStats(showLoading = true) {
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_API_ENDPOINT}/api/scans/scan-stats`,
          {
            headers: {
              'Authorization': 'Bearer ' + this.$store.state.auth.jwt
            }
          }
        );
        
        if (response.data.success) {
          this.scanStats = response.data.data;
          // Check if user has any scans
          this.hasData = this.scanStats.totalScans > 0;
        }
      } catch (error) {
        console.error('Error fetching scan stats:', error);
      }
    },
    async fetchAllScans(showLoading = true) {
      if (showLoading) {
        this.fetchingTable = true;
      }
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_API_ENDPOINT}/api/scans/all-scans`,
          {
            params: {
              page: this.page,
              limit: this.itemsPerPage,
              search: this.searchQuery,
              scanType: this.scanTypeFilter
            },
            headers: {
              'Authorization': 'Bearer ' + this.$store.state.auth.jwt
            }
          }
        );

        if (response.data.success) {
          this.originalScans = response.data.data || [];
          this.scans = [...this.originalScans]; // Create a copy for sorting
          this.totalItems = (response.data.pagination && response.data.pagination.totalItems) || 0;
          this.totalPages = (response.data.pagination && response.data.pagination.totalPages) || 1;
          // Apply current sort to the new data
          this.sortScans();
        } else {
          // Reset values on error
          this.scans = [];
          this.originalScans = [];
          this.totalItems = 0;
          this.totalPages = 1;
        }
      } catch (error) {
        console.error('Error fetching scans:', error);
        // Reset values on error
        this.scans = [];
        this.originalScans = [];
        this.totalItems = 0;
        this.totalPages = 1;
      } finally {
        if (showLoading) {
          this.fetchingTable = false;
        }
      }
    },
    newScan() {
      if (this.isEmbedMode) {
        // In embed mode, preserve the query parameters
        this.$router.push({
          path: '/create-scan',
          query: {
            embed: 'true',
            jwt: this.embedToken || this.$route.query.jwt
          }
        });
      } else {
        this.$router.push('/create-scan');
      }
    },
    viewReport(scan) {
      this.closeDropdowns();
      // Route based on scan type
      const route = scan.scanType === 'Recurring' ?
        `/recurring-report/${scan.id}` :
        `/scan-report/${scan.id}`;

      if (this.isEmbedMode) {
        // Navigate within iframe
        this.navigateInIframe(route);
      } else {
        // Open in new tab
        const routeData = this.$router.resolve(route);
        window.open(routeData.href, '_blank');
      }
    },
    viewGrid(scan) {
      this.closeDropdowns();
      // Route based on scan type
      // scanType is 'Quick Scan' or 'Recurring'
      const route = scan.scanType === 'Recurring' ?
        `/recurring-grid/${scan.id}` :
        `/view-grid/${scan.id}`;

      if (this.isEmbedMode) {
        // Navigate within iframe
        this.navigateInIframe(route);
      } else {
        // Open in new tab
        const routeData = this.$router.resolve(route);
        window.open(routeData.href, '_blank');
      }
    },
    async rerunScan(scanId) {
      this.closeDropdowns();

      try {
        // Show confirmation dialog
        const result = await this.$swal({
          title: 'Rescan Confirmation',
          text: 'Are you sure you want to rescan this location? This will create a new scan with the same settings.',
          icon: 'question',
          showCancelButton: true,
          confirmButtonColor: '#3085d6',
          cancelButtonColor: '#d33',
          confirmButtonText: 'Yes, rescan it!'
        });

        if (!result.isConfirmed) {
          return;
        }

        // Show loading
        this.$swal({
          title: 'Starting Rescan...',
          text: 'Please wait while we initialize the rescan',
          icon: 'info',
          allowOutsideClick: false,
          showConfirmButton: false,
          willOpen: () => {
            this.$swal.showLoading();
          }
        });

        // Call the rescan API
        const response = await axios.post(
          `${process.env.VUE_APP_API_ENDPOINT}/api/scans/rescan`,
          { scanId: scanId },
          {
            headers: {
              'Authorization': 'Bearer ' + this.$store.state.auth.jwt,
              'Content-Type': 'application/json'
            }
          }
        );

        if (response.data.success) {
          // Show success message
          await this.$swal({
            icon: 'success',
            title: 'Rescan Started!',
            text: 'The rescan has been initiated successfully.',
            timer: 2000,
            showConfirmButton: false
          });

          // Refresh the scans list
          await this.fetchAllScans();
          await this.fetchScanStats();

          // Navigate to the new scan
          if (response.data.search_id) {
            const route = `/view-grid/${response.data.search_id}`;
            if (this.isEmbedMode) {
              // Navigate within iframe
              this.navigateInIframe(route);
            } else {
              // Open in new tab
              const routeData = this.$router.resolve(route);
              window.open(routeData.href, '_blank');
            }
          }
        } else {
          this.$swal({
            icon: 'error',
            title: 'Error',
            text: response.data.message || 'Failed to start rescan'
          });
        }
      } catch (error) {
        console.error('Error starting rescan:', error);
        const errorMessage = (error.response && error.response.data && error.response.data.message) || 'Failed to start rescan';
        this.$swal({
          icon: 'error',
          title: 'Rescan Failed',
          text: errorMessage
        });
      }
    },
    async exportPNG(scanId) {
      this.closeDropdowns();

      try {
        // Find the scan in our list
        const scan = this.scans.find(s => s.id === scanId);
        if (!scan) {
          throw new Error('Scan not found');
        }

        // Check if it's a quick scan and still processing
        if (scan.scanType === 'Quick Scan' && scan.status !== 'completed') {
          this.$swal({
            icon: 'warning',
            title: 'Scan Not Ready',
            text: 'This scan is still processing. Please wait for it to complete before exporting.'
          });
          return;
        }

        // Show loading
        this.$swal({
          title: 'Generating PNG...',
          text: 'Please wait while we create your map image',
          icon: 'info',
          allowOutsideClick: false,
          showConfirmButton: false,
          willOpen: () => {
            this.$swal.showLoading();
          }
        });

        // Determine the endpoint based on scan type
        let dataEndpoint;
        if (scan.scanType === 'Recurring') {
          // For grid scans, get the latest execution
          const executionsResponse = await axios.get(
            `${process.env.VUE_APP_API_ENDPOINT}/api/scans/grid-scan/${scanId}/executions`,
            {
              headers: {
                'Authorization': 'Bearer ' + this.$store.state.auth.jwt
              }
            }
          );

          if (!executionsResponse.data.success || !executionsResponse.data.data.length) {
            throw new Error('No execution data available for this grid scan');
          }

          // Get the latest execution
          const latestExecution = executionsResponse.data.data[0];

          // Get the execution data with process items
          dataEndpoint = `${process.env.VUE_APP_API_ENDPOINT}/api/scans/grid-scan/${scanId}/execution/${latestExecution.id}`;
        } else {
          // For quick scans
          dataEndpoint = `${process.env.VUE_APP_API_ENDPOINT}/api/scans/get-grid-points/${scanId}`;
        }

        // Fetch the scan data with process items
        const response = await axios.get(dataEndpoint, {
          headers: {
            'Authorization': 'Bearer ' + this.$store.state.auth.jwt
          }
        });

        if (!response.data.success) {
          throw new Error('Failed to fetch scan data');
        }

        let processItems = [];
        let businessInfo = {};

        if (scan.scanType === 'Recurring') {
          // Extract data for grid scan
          processItems = response.data.data.processItems || [];
          businessInfo = (response.data.data.scan && response.data.data.scan.business) || {};
        } else {
          // Extract data for quick scan
          const scanData = response.data.data;
          if (scanData.ScanItems && scanData.ScanItems.length > 0) {
            scanData.ScanItems.forEach(scanItem => {
              if (scanItem.ProcessItems) {
                processItems.push(...scanItem.ProcessItems);
              }
            });
          }
          // Parse business info
          businessInfo = typeof scanData.business === 'string' ?
            JSON.parse(scanData.business) : scanData.business || {};
        }

        // Generate the PNG
        await this.generateMapPNG(processItems, businessInfo, scan);

        this.$swal.close();

      } catch (error) {
        console.error('Error exporting PNG:', error);
        this.$swal.close(); // Close the loading dialog first
        setTimeout(() => {
          this.$swal({
            icon: 'error',
            title: 'Export Failed',
            text: error.message || 'Failed to export PNG. Please try again.'
          });
        }, 100);
      }
    },

    async generateMapPNG(processItems, businessInfo, scan) {

      let mapContainer = null;
      try {
        // Create a hidden container for the map
        mapContainer = document.createElement('div');
        mapContainer.style.position = 'fixed';
        mapContainer.style.left = '0';
        mapContainer.style.top = '0';
        mapContainer.style.width = '1200px';
        mapContainer.style.height = '800px';
        mapContainer.style.backgroundColor = 'white';
        mapContainer.style.zIndex = '-9999';
        mapContainer.style.visibility = 'hidden';
        document.body.appendChild(mapContainer);

      // Create header section
      const headerSection = document.createElement('div');
      headerSection.style.padding = '20px';
      headerSection.style.backgroundColor = '#f8f9fa';
      headerSection.style.borderBottom = '1px solid #dee2e6';
      headerSection.innerHTML = `
        <div style="display: flex; justify-content: space-between; align-items: center;">
          <div>
            <h2 style="margin: 0; color: #212529; font-size: 24px;">${businessInfo.business_name || scan.businessName || 'Business'}</h2>
            <p style="margin: 5px 0 0 0; color: #6c757d; font-size: 14px;">${businessInfo.address || scan.address || ''}</p>
          </div>
          <div style="text-align: right;">
            <div style="color: #6c757d; font-size: 12px;">Scan Type: ${scan.scanType}</div>
            <div style="color: #6c757d; font-size: 12px;">Date: ${new Date().toLocaleDateString()}</div>
          </div>
        </div>
      `;
      mapContainer.appendChild(headerSection);

      // Create map div
      const mapDiv = document.createElement('div');
      mapDiv.style.width = '100%';
      mapDiv.style.height = '700px';
      mapContainer.appendChild(mapDiv);

      // Initialize Google Maps
      const userApiKey = this.$store.getters['apiKeys/googleMapsKey'];
      if (!userApiKey) {
        document.body.removeChild(mapContainer);
        throw new Error('Google Maps API key not configured');
      }

      // Wait for Google Maps to be ready
      if (!window.google || !window.google.maps) {
        await new Promise((resolve, reject) => {
          const script = document.createElement('script');
          script.src = `https://maps.googleapis.com/maps/api/js?key=${userApiKey}`;
          script.async = true;
          script.onload = resolve;
          script.onerror = reject;
          document.head.appendChild(script);
        });
      }

      // Calculate center from process items
      let centerLat = 0, centerLng = 0, validCount = 0;
      processItems.forEach(item => {
        if (item.latitude && item.longitude) {
          centerLat += parseFloat(item.latitude);
          centerLng += parseFloat(item.longitude);
          validCount++;
        }
      });

      if (validCount > 0) {
        centerLat /= validCount;
        centerLng /= validCount;
      } else {
        // Default center if no valid coordinates
        centerLat = businessInfo.latitude || 28.5887354;
        centerLng = businessInfo.longitude || 77.0829215;
      }

      // Make container visible for map initialization
      mapContainer.style.visibility = 'visible';
      mapContainer.style.zIndex = '-1';

      // Create map without controls for clean PNG export
      const map = new google.maps.Map(mapDiv, {
        zoom: 14,
        center: { lat: centerLat, lng: centerLng },
        mapTypeId: 'roadmap',
        disableDefaultUI: true,
        zoomControl: false,
        mapTypeControl: false,
        scaleControl: false,
        streetViewControl: false,
        fullscreenControl: false
      });

      // Trigger resize to ensure map renders
      google.maps.event.trigger(map, 'resize');

      // Draw grid overlay (circle or square)
      const gridType = businessInfo.grid_shape || businessInfo.gridType || scan.gridShape || 'square';

      // Calculate grid bounds from process items
      if (processItems.length > 0) {
        if (gridType === 'circle' || gridType === 'circular') {
          // Calculate radius from center to farthest point
          let maxDistance = 0;
          processItems.forEach(item => {
            if (item.latitude && item.longitude) {
              const distance = this.getDistanceFromLatLonInMeters(
                centerLat, centerLng,
                parseFloat(item.latitude), parseFloat(item.longitude)
              );
              maxDistance = Math.max(maxDistance, distance);
            }
          });

          // Add 10% padding
          const radius = maxDistance * 1.1;

          // Draw circle
          new google.maps.Circle({
            center: { lat: centerLat, lng: centerLng },
            radius: radius,
            fillColor: '#2477f6',
            fillOpacity: 0.05,
            strokeColor: '#2477f6',
            strokeOpacity: 0.8,
            strokeWeight: 2.5,
            map: map
          });
        } else {
          // Calculate square bounds
          let minLat = Infinity, maxLat = -Infinity;
          let minLng = Infinity, maxLng = -Infinity;

          processItems.forEach(item => {
            if (item.latitude && item.longitude) {
              const lat = parseFloat(item.latitude);
              const lng = parseFloat(item.longitude);
              minLat = Math.min(minLat, lat);
              maxLat = Math.max(maxLat, lat);
              minLng = Math.min(minLng, lng);
              maxLng = Math.max(maxLng, lng);
            }
          });

          // Add padding
          const latPadding = (maxLat - minLat) * 0;
          const lngPadding = (maxLng - minLng) * 0;

          // Draw rectangle
          new google.maps.Rectangle({
            bounds: {
              north: maxLat + latPadding,
              south: minLat - latPadding,
              east: maxLng + lngPadding,
              west: minLng - lngPadding
            },
            fillColor: '#2477f6',
            fillOpacity: 0.05,
            strokeColor: '#2477f6',
            strokeOpacity: 0.8,
            strokeWeight: 2.5,
            map: map
          });
        }
      }

      // Add markers for process items
      const bounds = new google.maps.LatLngBounds();
      let markersAdded = 0;
      processItems.forEach((item, index) => {
        if (item.latitude && item.longitude) {
          const position = new google.maps.LatLng(
            parseFloat(item.latitude),
            parseFloat(item.longitude)
          );

          const rank = item.rank || 0;
          const markerColor = this.getMarkerColor(rank);
          const displayText = rank === 0 || rank > 20 ? '20+' : rank.toString();

          try {
            new google.maps.Marker({
              position: position,
              map: map,
              icon: {
                url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(this.createMarkerSVG(markerColor, displayText))}`,
                scaledSize: new google.maps.Size(32, 48),
                anchor: new google.maps.Point(16, 48)
              }
            });
            markersAdded++;
            bounds.extend(position);
          } catch (markerError) {
            console.error('Error creating marker', index, markerError);
          }
        }
      });

      // Fit map to bounds
      if (markersAdded > 0) {
        map.fitBounds(bounds);
      }

      // Wait for map to load with timeout
      if (google.maps && google.maps.event) {
        await new Promise((resolve, reject) => {
          const timeout = setTimeout(() => {
            resolve();
          }, 3000); // 3 second timeout

          google.maps.event.addListenerOnce(map, 'idle', () => {
            clearTimeout(timeout);
            resolve();
          });
        });
      } else {
        await new Promise(resolve => setTimeout(resolve, 2000));
      }

      // Additional wait for tiles to load
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Import html2canvas dynamically
      const html2canvas = (await import('html2canvas')).default;

      // Hide container from view but keep it rendered
      mapContainer.style.position = 'absolute';
      mapContainer.style.left = '-9999px';

      // Capture the container
      const canvas = await html2canvas(mapContainer, {
        useCORS: true,
        allowTaint: true,
        scale: 2,
        logging: false,
        backgroundColor: '#ffffff'
      });

      // Remove temporary container
      document.body.removeChild(mapContainer);

      // Convert to blob and download
      canvas.toBlob((blob) => {
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.href = url;

        const businessName = (businessInfo.business_name || scan.businessName || 'scan')
          .replace(/[^a-z0-9]/gi, '_');
        const scanType = scan.scanType === 'Recurring' ? 'grid_scan' : 'quick_scan';
        const dateStr = new Date().toISOString().split('T')[0];

        link.download = `${scanType}_${businessName}_${dateStr}.png`;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        this.$swal({
          icon: 'success',
          title: 'Export Successful',
          text: 'Your PNG has been downloaded',
          timer: 2000,
          showConfirmButton: false
        });
      }, 'image/png');

      } catch (error) {
        console.error('Error in generateMapPNG:', error);
        // Clean up the container if it exists
        if (mapContainer && mapContainer.parentNode) {
          document.body.removeChild(mapContainer);
        }
        // Re-throw to be handled by the calling function
        throw error;
      }
    },

    getMarkerColor(rank) {
      if (!rank || rank === 0 || rank > 20) return '#dcdbdb';
      if (rank <= 5) return '#3EBB80'; // Green for 1-5
      if (rank <= 14) return '#f59e0b'; // Orange for 6-14
      if (rank <= 20) return '#FF0000'; // Red for 15-20
      return '#dcdbdb';
    },

    getDistanceFromLatLonInMeters(lat1, lon1, lat2, lon2) {
      const R = 6371000; // Radius of the earth in meters
      const dLat = this.deg2rad(lat2 - lat1);
      const dLon = this.deg2rad(lon2 - lon1);
      const a =
        Math.sin(dLat/2) * Math.sin(dLat/2) +
        Math.cos(this.deg2rad(lat1)) * Math.cos(this.deg2rad(lat2)) *
        Math.sin(dLon/2) * Math.sin(dLon/2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
      const d = R * c;
      return d;
    },

    deg2rad(deg) {
      return deg * (Math.PI/180);
    },

    createMarkerSVG(color, text) {
      return `
        <svg width="32" height="48" viewBox="0 0 32 48" xmlns="http://www.w3.org/2000/svg">
          <g>
            <path d="M16 0C7.16 0 0 7.16 0 16c0 11.2 14.4 30.4 15.36 31.52c0.16 0.16 0.4 0.32 0.64 0.32s0.48-0.16 0.64-0.32C17.6 46.4 32 27.2 32 16c0-8.84-7.16-16-16-16z" fill="${color}" stroke="#888888" stroke-width="1"/>
            <text x="16" y="21" text-anchor="middle" fill="#FFFFFF" font-size="13" font-weight="600" font-family="Arial" stroke="#333333" stroke-width="2" paint-order="stroke fill">${text}</text>
          </g>
        </svg>
      `;
    },
    async exportCSV(scanId) {
      this.closeDropdowns();

      try {
        // Find the scan to determine its type
        const scan = this.scans.find(s => s.id === scanId);
        if (!scan) {
          throw new Error('Scan not found');
        }
        
        // Show loading
        this.$swal({
          title: 'Exporting CSV...',
          text: 'Please wait while we prepare your export',
          icon: 'info',
          allowOutsideClick: false,
          showConfirmButton: false,
          willOpen: () => {
            this.$swal.showLoading();
          }
        });

        // Determine the correct export endpoint based on scan type
        const exportEndpoint = scan.scanType === 'Recurring' ? 
          `${process.env.VUE_APP_API_ENDPOINT}/api/scans/export-grid-csv/${scanId}` :
          `${process.env.VUE_APP_API_ENDPOINT}/api/scans/export-csv/${scanId}`;

        // Fetch detailed scan data including process items
        const response = await axios.get(
          exportEndpoint,
          {
            headers: {
              'Authorization': 'Bearer ' + this.$store.state.auth.jwt
            },
            responseType: 'blob' // Important for file download
          }
        );

        // Create download link
        const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        
        // Get scan info for filename
        const businessName = scan.businessName ? 
          scan.businessName.replace(/[^a-z0-9]/gi, '_') : 'scan';
        const scanType = scan.scanType === 'Recurring' ? 'grid_scan' : 'quick_scan';
        const dateStr = new Date().toISOString().split('T')[0];
        
        link.setAttribute('download', `${scanType}_${businessName}_${dateStr}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        // Clean up
        URL.revokeObjectURL(url);
        
        // Show success
        this.$swal({
          icon: 'success',
          title: 'Export Successful',
          text: 'Your CSV file has been downloaded',
          timer: 2000,
          showConfirmButton: false
        });
      } catch (error) {
        console.error('Error exporting CSV:', error);
        
        // Check if error response is JSON or text
        let errorMessage = 'Failed to export CSV';
        if (error.response) {
          if (error.response.data instanceof Blob) {
            // If response is a blob, try to read it as text
            try {
              const text = await error.response.data.text();
              const jsonError = JSON.parse(text);
              errorMessage = jsonError.message || errorMessage;
            } catch (e) {
              // If can't parse, use default message
            }
          } else if (error.response.data && error.response.data.message) {
            errorMessage = error.response.data.message;
          }
        }
        
        this.$swal({
          icon: 'error',
          title: 'Export Failed',
          text: errorMessage
        });
      }
    },
    shareScan(scanId) {
      // Close dropdown menu first
      this.closeDropdowns();

      // Find the scan to determine its type
      const scan = this.scans.find(s => s.id === scanId);
      if (scan) {
        this.shareScanId = scanId;
        this.shareScanType = scan.scan_type; // 'quick_scan' or 'grid_scan'
        this.showShareModal = true;
      }
    },
    closeShareModal() {
      this.showShareModal = false;
      this.shareScanId = null;
      this.shareScanType = null;
    },
    onShareCopied(type) {
      const message = type === 'grid' ? 'Grid URL copied to clipboard!' : 
                      type === 'report' ? 'Report URL copied to clipboard!' : 
                      'Embed code copied to clipboard!';
      
      this.$swal({
        icon: 'success',
        title: 'Copied!',
        text: message,
        timer: 2000,
        showConfirmButton: false,
        toast: true,
        position: 'top-end'
      });
    },
    editScan(scanId) {
      this.closeDropdowns();

      if (this.isEmbedMode) {
        // Navigate within iframe for embedded mode
        this.$router.push({
          path: '/create-scan',
          query: {
            editId: scanId,
            mode: 'edit',
            embed: 'true',
            jwt: this.embedToken || this.$route.query.jwt
          }
        });
      } else {
        // Open in new tab for normal mode
        const routeData = this.$router.resolve({
          path: '/create-scan',
          query: {
            editId: scanId,
            mode: 'edit'
          }
        });
        window.open(routeData.href, '_blank');
      }
    },
    duplicateScan(scanId) {
      this.closeDropdowns();

      if (this.isEmbedMode) {
        // Navigate within iframe for embedded mode
        this.$router.push({
          path: '/create-scan',
          query: {
            duplicateFrom: scanId,
            embed: 'true',
            jwt: this.embedToken || this.$route.query.jwt
          }
        });
      } else {
        // Open in new tab for normal mode
        const routeData = this.$router.resolve({
          path: '/create-scan',
          query: { duplicateFrom: scanId }
        });
        window.open(routeData.href, '_blank');
      }
    },
    embedScan(id) {
      // TODO: Show embed code modal
    },
    deleteScan(id) {
      this.closeDropdowns();
      this.deleteType = 'single';
      this.deleteIds = [id];
      this.deleteItemCount = 1;
      this.showDeleteDialog = true;
    },
    bulkDelete() {
      this.closeDropdowns();
      if (this.selectedIds.length === 0) {
        // You can add a notification here if needed
        return;
      }
      this.deleteType = 'bulk';
      this.deleteIds = [...this.selectedIds];
      this.deleteItemCount = this.selectedIds.length;
      this.showDeleteDialog = true;
    },
    async confirmDelete() {
      this.deleting = true;
      try {
        // Get scan details before deletion for tracking
        const scansToDelete = this.scans.filter(s => this.deleteIds.includes(s.id));

        const response = await axios.delete(
          `${process.env.VUE_APP_API_ENDPOINT}/api/scans/delete`,
          {
            data: { scanIds: this.deleteIds },
            headers: {
              'Authorization': 'Bearer ' + this.$store.state.auth.jwt
            }
          }
        );

        if (response.data.success) {
          // Track scan deletion
          const { trackUserJourney } = await import('@/utils/userJourney');
          trackUserJourney('gg_scan_deleted', {
            scan_ids: scansToDelete.map(s => s.id),
            scan_count: scansToDelete.length,
            scan_types: scansToDelete.map(s => s.scanType || s.scan_type)
          });

          // Remove deleted items from both arrays
          this.deleteIds.forEach(id => {
            const index = this.scans.findIndex(s => s.id === id);
            if (index > -1) this.scans.splice(index, 1);

            const origIndex = this.originalScans.findIndex(s => s.id === id);
            if (origIndex > -1) this.originalScans.splice(origIndex, 1);
          });

          // Refresh stats
          await this.fetchScanStats();

          // Clear selection if bulk delete
          if (this.deleteType === 'bulk') {
            this.selectedIds = [];
            this.selectAll = false;
          }

          // Close dialog
          this.showDeleteDialog = false;

          // You can add a success notification here
          // Deleted successfully
        }
      } catch (error) {
        console.error('Error deleting scans:', error);
        // You can add an error notification here
      } finally {
        this.deleting = false;
      }
    },
    cancelDelete() {
      this.showDeleteDialog = false;
      this.deleteIds = [];
      this.deleteItemCount = 0;
    },
    toggleSelectAll() {
      if (this.selectAll) {
        this.selectedIds = this.scans.map(scan => scan.id)
      } else {
        this.selectedIds = []
      }
    },
    formatKeywords(keywords) {
      if (!keywords || keywords.length === 0) return '-';
      if (keywords.length === 1) return keywords[0];
      return `${keywords[0]} +${keywords.length - 1}`;
    },
    showTooltip(event, keywords) {
      // Remove any existing tooltip
      this.hideTooltip();

      // Create tooltip element with inline styles
      const tooltip = document.createElement('div');
      tooltip.id = 'scans-keyword-tooltip';

      // Apply inline styles to tooltip
      Object.assign(tooltip.style, {
        position: 'fixed',
        backgroundColor: '#19233A',
        color: 'white',
        padding: '20px 20px',
        borderRadius: '10px',
        fontSize: '12px',
        zIndex: '9999',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        maxWidth: '400px'
      });

      // Create keyword items with inline styling
      const keywordsHtml = keywords.map(k =>
        `<span style="
          display: inline-block;
          padding: 5px 15px;
          border: 1px solid white;
          border-radius: 10px;
          text-transform: capitalize;
          white-space: nowrap;
          font-size: 14px;
          margin: 0 3px;
        ">${k}</span>`
      ).join('');

      tooltip.innerHTML = `
        <div style="display: flex; flex-wrap: wrap; gap: 6px; align-items: center;">
          ${keywordsHtml}
        </div>
      `;

      // Position tooltip at cursor position
      document.body.appendChild(tooltip);
      const tooltipRect = tooltip.getBoundingClientRect();

      // Get cursor position from event
      const cursorX = event.clientX;
      const cursorY = event.clientY;

      // Center tooltip horizontally on cursor
      let tooltipLeft = cursorX - (tooltipRect.width / 2);

      // Ensure tooltip doesn't go off screen
      if (tooltipLeft < 10) {
        tooltipLeft = 10;
      } else if (tooltipLeft + tooltipRect.width > window.innerWidth - 10) {
        tooltipLeft = window.innerWidth - tooltipRect.width - 10;
      }

      tooltip.style.left = tooltipLeft + 'px';
      tooltip.style.top = (cursorY + 15) + 'px'; // 15px below cursor

      // Add arrow pointing to cursor position
      const arrowLeft = Math.max(10, Math.min(tooltipRect.width - 10, cursorX - tooltipLeft));
      const arrow = document.createElement('div');
      Object.assign(arrow.style, {
        position: 'absolute',
        top: '-8px',
        left: (arrowLeft - 8) + 'px',
        width: '0',
        height: '0',
        borderLeft: '8px solid transparent',
        borderRight: '8px solid transparent',
        borderBottom: '8px solid #19233A'
      });
      tooltip.appendChild(arrow);
    },
    hideTooltip() {
      const tooltip = document.getElementById('scans-keyword-tooltip');
      if (tooltip) {
        tooltip.remove();
      }
    },
    showLastRunTooltip(event, scan) {
      // Remove any existing tooltip
      this.hideLastRunTooltip();

      // Create tooltip element with inline styles
      const tooltip = document.createElement('div');
      tooltip.id = 'scans-lastrun-tooltip';

      // Apply inline styles to tooltip
      Object.assign(tooltip.style, {
        position: 'fixed',
        backgroundColor: '#19233A',
        color: 'white',
        padding: '12px 16px',
        borderRadius: '10px',
        fontSize: '12px',
        zIndex: '9999',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        whiteSpace: 'nowrap'
      });

      // Format last run date or show "Not run"
      const lastRunText = scan.lastRunAt ?
        `Last Run: <br>${this.formatDate(scan.lastRunAt)}` :
        'Last Run: Not run';

      tooltip.innerHTML = `<div>${lastRunText}</div>`;

      // Position tooltip at cursor position
      document.body.appendChild(tooltip);
      const tooltipRect = tooltip.getBoundingClientRect();

      // Get cursor position from event
      const cursorX = event.clientX;
      const cursorY = event.clientY;

      // Center tooltip horizontally on cursor
      let tooltipLeft = cursorX - (tooltipRect.width / 2);

      // Ensure tooltip doesn't go off screen
      if (tooltipLeft < 10) {
        tooltipLeft = 10;
      } else if (tooltipLeft + tooltipRect.width > window.innerWidth - 10) {
        tooltipLeft = window.innerWidth - tooltipRect.width - 10;
      }

      tooltip.style.left = tooltipLeft + 'px';
      tooltip.style.top = (cursorY + 15) + 'px'; // 15px below cursor

      // Add arrow pointing to cursor position
      const arrowLeft = Math.max(10, Math.min(tooltipRect.width - 10, cursorX - tooltipLeft));
      const arrow = document.createElement('div');
      Object.assign(arrow.style, {
        position: 'absolute',
        top: '-8px',
        left: (arrowLeft - 8) + 'px',
        width: '0',
        height: '0',
        borderLeft: '8px solid transparent',
        borderRight: '8px solid transparent',
        borderBottom: '8px solid #19233A'
      });
      tooltip.appendChild(arrow);
    },
    hideLastRunTooltip() {
      const tooltip = document.getElementById('scans-lastrun-tooltip');
      if (tooltip) {
        tooltip.remove();
      }
    },
    formatScanSize(scan) {
      if (scan.search_type === 'quick_scan') {
        return `${scan.grid_size || '3 x 3'} • ${scan.radius || 1} Miles`;
      }
      return '-';
    },
    formatSchedule(scan) {
      if (scan.scanType === 'Recurring') {
        return scan.frequency || 'Weekly';
      }
      return '-';
    },
    formatDate(date) {
      return moment(date).format('MMM D, YYYY h:mm A')
    },
    formatRadius(scan) {
      let radius = parseFloat(scan.radius) || 1;
      const distanceType = scan.distanceType || 'miles';

      // Round to a reasonable precision
      if (radius < 1) {
        radius = Math.round(radius * 10) / 10; // Round to 1 decimal place for small values
      } else {
        radius = Math.round(radius); // Round to whole number for larger values
      }

      // Format the distance with proper unit
      if (distanceType.toLowerCase() === 'kilometers' || distanceType.toLowerCase() === 'km') {
        // For kilometers, use singular/plural form
        return radius === 1 ? `${radius} km` : `${radius} km`;
      } else {
        // For miles, use singular/plural form
        return radius === 1 ? `${radius} Mile` : `${radius} Miles`;
      }
    },
    toggleSort(field) {
      if (this.sortBy === field) {
        this.sortDesc = !this.sortDesc;
      } else {
        this.sortBy = field;
        this.sortDesc = false;
      }
      this.sortScans();
    },
    sortScans() {
      if (!this.scans || this.scans.length === 0) return;

      const sorted = [...this.scans].sort((a, b) => {
        let aVal = a[this.sortBy];
        let bVal = b[this.sortBy];

        // Handle special cases
        if (this.sortBy === 'keywords') {
          aVal = (a.keywords && a.keywords[0]) || '';
          bVal = (b.keywords && b.keywords[0]) || '';
        }

        // Handle null/undefined values
        if (aVal === null || aVal === undefined) aVal = '';
        if (bVal === null || bVal === undefined) bVal = '';

        // Convert to lowercase for string comparison (but not for numbers)
        if (typeof aVal === 'string') aVal = aVal.toLowerCase();
        if (typeof bVal === 'string') bVal = bVal.toLowerCase();

        // Compare
        if (aVal < bVal) return this.sortDesc ? 1 : -1;
        if (aVal > bVal) return this.sortDesc ? -1 : 1;
        return 0;
      });

      this.scans = sorted;
    },
    trackEmbedUsage() {
      try {
        const embedInfo = {
          page: 'scans',
          referrer: document.referrer || null,
          timestamp: new Date().toISOString()
        };

        // Check for ancestorOrigins (Chrome/Edge only)
        if (window.location.ancestorOrigins && window.location.ancestorOrigins.length > 0) {
          embedInfo.parentOrigin = window.location.ancestorOrigins[0];
        }

        // TODO: Optionally send to backend for analytics
        // this.$axios.post('/api/analytics/embed-usage', embedInfo);
      } catch (error) {
        // Silent fail
      }
    }
  },
  watch: {
    searchQuery(newVal) {
      clearTimeout(this.searchDebounce);
      this.searchDebounce = setTimeout(() => {
        this.page = 1;
        this.fetchAllScans();
      }, 500);
    },
    scanTypeFilter() {
      this.page = 1;
      this.fetchAllScans();
    },
    page() {
      this.fetchAllScans();
    }
  },
  async mounted() {
    // Add click listener to close dropdowns when clicking outside
    document.addEventListener('click', this.closeDropdowns);

    // Track embed usage if in embedded mode
    if (this.isEmbedMode) {
      this.trackEmbedUsage();
    }

    // Fetch stats data (initial load with loading state)
    this.fetchingData = true;
    await this.fetchScanStats(true);
    this.fetchingData = false;

    // Fetch table data if user has scans (with loading state)
    if (this.hasData) {
      await this.fetchAllScans(true);
    }

    // Set up auto-refresh every 30 seconds
    this.refreshInterval = setInterval(async () => {
      // Only refresh if not currently fetching and user has data
      if (!this.fetchingTable && !this.fetchingData && this.hasData) {
        await this.refreshData();
      }
    }, 30000); // 30 seconds
  },
  beforeDestroy() {
    // Clear the refresh interval
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
    }
    // Remove click listener
    document.removeEventListener('click', this.closeDropdowns);
    // Clean up any remaining tooltips
    this.hideTooltip();
    this.hideLastRunTooltip();
  }
}
</script>

<style lang="scss" scoped>
// Embed mode styles
.embed-container {
  background: #f5f5f5;
  min-height: 100vh;
  padding: 20px;
}

// Import global styles from reference
.dashboard-box {
  position: relative;
}

.dashboard-box .v-sheet.v-card {
  border-radius: 5px !important;
  box-shadow: 0px 2px 20px rgba(62, 187, 128, 0.1) !important;
  border: 0px !important;
}

.dashboard-box .dashboard-box-title {
  font-size: 14px;
  align-items: center;
  color: #19233A;
  font-weight: bold;
  opacity: 0.5;
  margin-bottom: 10px;
}

.stats-box .stats-box-number {
  color: #19233A;
  font-size: 30px;
  font-weight: bold;
  padding-top: 0px;
}

.stats-box .stats-box-icon-bg {
  position: absolute;
  background: #3EBB80;
  opacity: 0.1;
  border-radius: 10px;
  width: 60px;
  height: 60px;
  right: 22px;
  top: 50%;
  margin-top: -30px;
}

.stats-box .stats-box-icon {
  position: absolute;
  border-radius: 10px;
  width: 30px;
  height: 30px;
  right: 36px;
  top: 50%;
  margin-top: -20px;
}

.stats-box .stats-box-icon i {
  font-size: 28px;
  color: #3EBB80;
}

.dashboard-box-iconsquare {
  width: 60px;
  height: 60px;
  position: relative;
  margin: auto;
}

.dashboard-box-iconsquare .dashboard-box-icon-background {
  border-radius: 10px;
  position: absolute;
  background: #3EBB80;
  opacity: 0.1;
  width: 100%;
  height: 100%;
}

.dashboard-box-iconsquare .dashboard-box-icon {
  position: absolute;
  width: 100%;
  height: 100%;
  text-align: center;
  line-height: 60px;
}

.dashboard-box-iconsquare .dashboard-box-icon i {
  font-size: 28px;
  color: #3EBB80;
}

table.rg-datatable {
  font-size: 12px;
}

table.rg-datatable th {
  background-color: #F7F8F8;
  border: solid 1px #F7F8F8;
  text-transform: uppercase;
  font-size: 11px;
  font-weight: 600;
  color: #19233A;
}

table.rg-datatable th, table.rg-datatable td {
  padding: 10px;
}

table.rg-datatable tr:nth-child(even) td {
  background-color: #FBFBFB;
}

.rg-datatable tr td, .rg-datatable tr th {
  border-bottom: 0px !important;
  font-size: 12px !important;
}

.table-menu-actions {
  padding-top: 0px;
  padding-bottom: 0px;
}

.table-menu-actions .v-list-item {
  min-height: 30px;
  padding-left: 6px;
  min-width: 150px;
  padding-right: 0px;
}

.table-menu-actions a {
  color: #19233A !important;
  font-size: 12px;
  display: block;
  width: 100%;
}

.table-menu-action-icon {
  display: inline-block;
  width: 20px;
  text-align: center;
}

.table-menu-actions a i {
  opacity: 0.6;
  font-size: 10px;
}

/* Custom dropdown styles */
/* Action buttons container */
/* Responsive table wrapper */
.table-responsive {
  width: 100%;
  overflow-x: auto;
  overflow-y: visible; /* Allow dropdowns to overflow vertically */
  -webkit-overflow-scrolling: touch;
  position: relative;
}

.table-responsive table {
  min-width: 1000px; /* Minimum width to prevent squishing */
}

.action-buttons {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
}

/* Icon buttons styling */
.action-icon-btn {
  background: transparent;
  border: none;
  padding: 4px 8px;
  border-radius: 4px;
  color: #757575;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  i {
    font-size: 18px;
  }

  &:hover {
    background: #f5f5f5;
    color: #3b82f6;
  }

  &:active {
    transform: scale(0.95);
  }
}

/* New button style with icon on top and text below */
.action-btn-with-text {
  background: transparent;
  border: none;
  padding: 6px 10px;
  border-radius: 4px;
  color: #757575;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  min-width: 65px;

  i {
    font-size: 18px;
    line-height: 1;
  }

  span {
    font-size: 11px;
    line-height: 1;
    white-space: nowrap;
  }

  &:hover {
    background: #f5f5f5;
    color: #3b82f6;
  }

  &:active {
    transform: scale(0.95);
  }
}

.custom-dropdown {
  position: relative;
  display: inline-block;
}

.custom-dropdown-content {
  position: fixed;
  background-color: white;
  min-width: 180px;
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.15);
  border-radius: 8px;
  z-index: 9999;
  text-align: left;
  overflow: hidden;
}

/* Dropdown opens upward when near bottom - position set dynamically via JavaScript */

.custom-dropdown-content .v-list {
  background-color: transparent !important;
  padding: 8px 0 !important;
}

/* Custom Select Dropdown Styles */
.custom-select-wrapper {
  position: relative;
  display: inline-block;
  min-width: 200px;
}

.custom-select {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  border: 1px solid rgba(0, 0, 0, 0.42);
  border-radius: 4px;
  background: white;
  cursor: pointer;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.6);
  min-height: 40px;
  transition: all 0.2s ease;
}

.custom-select:hover {
  border-color: rgba(0, 0, 0, 0.87);
  color: rgba(0, 0, 0, 0.87);
}

.custom-select.active {
  border-color: #1976d2;
  color: #1976d2;
}

.custom-select-label {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.custom-select-arrow {
  font-size: 16px;
  color: inherit;
  transition: transform 0.2s ease;
}

.custom-select.active .custom-select-arrow {
  transform: rotate(180deg);
}

.custom-select-dropdown {
  position: absolute;
  top: calc(100% + 2px);
  left: 0;
  right: 0;
  background: white;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  overflow: hidden;
}

.custom-select-option {
  padding: 10px 12px;
  cursor: pointer;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.87);
  transition: background-color 0.2s ease;
}

.custom-select-option:hover {
  background-color: #f5f5f5;
}

.custom-select-option.selected {
  background-color: #e3f2fd;
  color: #1976d2;
  font-weight: 500;
}

/* Custom Actions Dropdown Styles */
.custom-actions-wrapper {
  position: relative;
  display: inline-block;
}

.custom-actions-button {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 16px;
  border: 1px solid rgba(0, 0, 0, 0.42);
  border-radius: 4px;
  background: white;
  cursor: pointer;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.6);
  transition: all 0.2s ease;
}

.custom-actions-button:hover {
  border-color: rgba(0, 0, 0, 0.87);
  color: rgba(0, 0, 0, 0.87);
}

.custom-actions-button.active {
  border-color: #1976d2;
  color: #1976d2;
}

.custom-actions-arrow {
  font-size: 16px;
  transition: transform 0.2s ease;
}

.custom-actions-button.active .custom-actions-arrow {
  transform: rotate(180deg);
}

.custom-actions-dropdown {
  position: absolute;
  top: calc(100% + 2px);
  right: 0;
  background: white;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  min-width: 150px;
  overflow: hidden;
}

.custom-actions-option {
  padding: 10px 16px;
  cursor: pointer;
  font-size: 12px;
  color: #19233A;
  transition: background-color 0.2s ease;
  display: flex;
  align-items: center;
}

.custom-actions-option:hover {
  background-color: #f5f5f5;
}

/* Center checkbox in table cell */
.checkbox-cell {
  text-align: center !important;
  vertical-align: middle !important;
}

.checkbox-cell .v-input {
  justify-content: center;
  margin: 0 auto;
}

.checkbox-cell .v-input__slot {
  margin: 0 !important;
}

.checkbox-cell .v-input--checkbox {
  align-items: center;
  justify-content: center;
}

/* Sortable header styles */
.sortable-header {
  cursor: pointer;
  user-select: none;
  text-transform: capitalize !important;
}

.sortable-header:hover {
  background-color: #F0F1F1 !important;
}

.header-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 4px;
}

.header-content span {
  text-transform: capitalize;
}

.sort-icon {
  margin-left: 4px;
  flex-shrink: 0;
}

/* Media Queries for Desktop Responsiveness */
@media (max-width: 1440px) {
  /* MacBook Air/Pro 13" */
  .table-responsive table {
    min-width: 950px;
  }

  .business-name {
    max-width: 200px !important;
  }
}

@media (max-width: 1366px) {
  /* Common laptop resolution */
  .filters-container {
    flex-wrap: wrap;
    gap: 10px;
  }

  .custom-select-wrapper {
    min-width: 150px !important;
  }
}

@media (max-width: 1280px) {
  /* Smaller laptops */
  .action-icon-btn i {
    font-size: 16px !important;
  }

  .action-buttons {
    gap: 2px;
  }
}

@media (min-width: 1920px) {
  /* Full HD and larger */
  .table-responsive table {
    min-width: auto;
  }

  .business-name {
    max-width: 350px !important;
  }
}


/* Mobile Responsiveness */
@media (max-width: 768px) {
  /* Show mobile header */
  .mobile-header {
    display: flex;
  }

  /* Add padding to content to avoid being hidden by fixed header (only when not in embed mode) */
  .panel-navigation-wrapper .content-section,
  .panel-navigation-wrapper h1.text-lg {
    margin-top: 70px; /* Space for the fixed header */
  }

  /* No margin-top for embed/iframe mode */
  .embed-container .content-section,
  .embed-container h1.text-lg {
    margin-top: 0 !important;
  }

  /* Remove ALL padding from table-responsive on mobile */
  .table-responsive {
    padding: 0 !important;
    margin: 0 !important;
    padding-left: 0 !important;
    padding-right: 0 !important;
    padding-top: 0 !important;
    padding-bottom: 0 !important;
  }

  /* Also target table container if it has padding */
  .table-container {
    padding: 0 !important;
  }

  .table-container .table-responsive {
    padding: 0 !important;
  }

  /* Stack stats cards vertically */
  .stats-grid {
    flex-direction: column !important;
  }

  .stats-box {
    width: 100% !important;
    margin-bottom: 10px !important;
  }

  /* Adjust filters for mobile */
  .filters-row {
    display: flex !important;
    flex-wrap: wrap !important;
    padding: 0 15px !important;
    align-items: flex-start !important;
  }

  /* Search field takes full width on its own line */
  .search-field {
    width: 100% !important;
    flex: 0 0 100% !important;
    margin-bottom: 10px !important;
    margin-right: 0 !important;
  }

  /* Hide the spacer and filter button on mobile */
  .filters-row .v-spacer {
    display: none !important;
  }

  .filter-btn {
    display: none !important;
  }

  /* FORCE both dropdowns on same line with 50% width each */
  .filter-dropdown {
    width: calc(50% - 5px) !important;
    max-width: calc(50% - 5px) !important;
    flex: 0 0 calc(50% - 5px) !important;
    margin-right: 10px !important;
    margin-left: 0 !important;
    display: block !important;
  }

  .actions-dropdown {
    width: calc(50% - 5px) !important;
    max-width: calc(50% - 5px) !important;
    flex: 0 0 calc(50% - 5px) !important;
    margin-left: 0 !important;
    margin-right: 0 !important;
    display: block !important;
  }

  /* Ensure the inner wrappers and buttons take full width */
  .custom-select-wrapper {
    width: 100% !important;
    min-width: unset !important;
    max-width: 100% !important;
  }

  .custom-actions-wrapper {
    width: 100% !important;
    min-width: unset !important;
    max-width: 100% !important;
  }

  .custom-select-button,
  .custom-actions-button {
    width: 100% !important;
    min-width: unset !important;
    justify-content: space-between !important;
    padding: 8px 12px !important;
  }

  /* Table adjustments for mobile */
  .table-container {
    padding: 0 15px !important;
    position: relative;
    overflow: hidden;
  }

  .table-responsive {
    overflow-x: auto !important;
    -webkit-overflow-scrolling: touch !important;
    margin: 0 -15px !important;
    padding: 0 15px !important;
  }

  .rg-datatable {
    min-width: 800px !important;
  }

  /* Fixed first columns for mobile */
  .rg-datatable thead tr {
    position: relative;
  }

  .rg-datatable tbody tr {
    position: relative;
  }

  /* Fix first two columns (checkbox and scan type) */
  .rg-datatable th:nth-child(1),
  .rg-datatable th:nth-child(2),
  .rg-datatable td:nth-child(1),
  .rg-datatable td:nth-child(2) {
    position: sticky !important;
    left: 0 !important;
    z-index: 5 !important; /* Lower z-index to stay below navigation */
    background: white !important;
  }

  .rg-datatable th:nth-child(2),
  .rg-datatable td:nth-child(2) {
    left: 50px !important; /* Width of first column */
    box-shadow: 2px 0 4px rgba(0,0,0,0.1) !important;
  }

  .rg-datatable tr:nth-child(even) td:nth-child(1),
  .rg-datatable tr:nth-child(even) td:nth-child(2) {
    background-color: #FBFBFB !important;
  }

  /* Action buttons adjustments */
  .action-btn-with-text {
    min-width: 55px !important;
    padding: 4px 8px !important;
  }

  .action-btn-with-text i {
    font-size: 16px !important;
  }

  .action-btn-with-text span {
    font-size: 10px !important;
  }

  /* Dropdown adjustments */
  .custom-dropdown-content {
    position: fixed !important;
    right: 10px !important;
    left: 10px !important;
    width: auto !important;
  }

  /* Pagination adjustments */
  .v-pagination {
    margin-top: 20px !important;
  }

  /* No data state adjustments */
  .dashboard-box-iconsquare {
    margin-top: 30px !important;
  }
}

/* Small mobile devices */
@media (max-width: 480px) {
  .mobile-title {
    font-size: 14px !important;
  }

  .new-scan-btn {
    padding: 10px 16px !important;
  }

  .new-scan-btn i {
    margin-right: 4px !important;
  }

  .stats-box-number {
    font-size: 24px !important;
  }

  .dashboard-box-title {
    font-size: 12px !important;
  }

  /* Hide "NEW SCAN" text, show only icon on very small screens */
  @media (max-width: 360px) {
    .new-scan-text {
      display: none !important;
    }
  }
}

/* Ensure navigation is responsive */
@media (max-width: 768px) {
  .rg-sidebar-left {
    transform: translateX(-100%);
    transition: transform 0.3s ease;
    z-index: 200 !important;
  }

  .rg-sidebar-left.mobile-open {
    transform: translateX(0);
  }

  /* Show overlay when nav is open */
  .nav-overlay {
    display: none;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0,0,0,0.5);
    z-index: 199;
  }

  .nav-overlay.show {
    display: block;
  }
}

</style>